import { isDefined } from '@sgme/fp';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { webapi, type WebapiEndpointBuilder } from '@/api/webapi.ts';
import { bulkTradeSlice } from '@/store/bulkTrade/slice.ts';
import type { BulkCashClientWay, BulkLeg, BulkLegId } from '@/store/bulkTrade/state.ts';
import type { AppDispatch } from '@/store/store.ts';
import type { ISODate, Tenor } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { IQuoteAbort, IQuoteReply, IQuoteWithdraw, ReceivedBulkQuoteReply } from '@/utils/rfs/type.ts';

export type BulkTradeRfsRequestParams = {
  tabId: string;
  rfsId: string;
  signalRConnectionId: string;
  clientId: number;
  currencyPair: string;
  negotiatedCurrencyIndex: CurrencyIndex;
  legs: Record<BulkLegId, BulkLeg>;
};

export type BulkTradeRfsRequestBody = {
  replyToStream: string;
  rfsId: string;
  requesterCounterpartyId: number;
  fxBulkProduct: {
    currencyPair: string;
    amountCurrency: CurrencyIndex;
    legs: Array<BulkTradeRfsRequestLeg>;
  };
};

export type BulkTradeRfsRequestLeg = {
  id: string;
  product: 'FxSpot' | 'FxFwd';
  way: BulkCashClientWay;
  amount: number;
  paymentDate: ISODate;
  paymentDateTenor: Tenor;
  ndFixingInfos: BulkTradeRfsRequestLegFixingInfos | null;
};

// In FX, always = null: NDF currency pairs are disabled in FX
export type BulkTradeRfsRequestLegFixingInfos = {
  settlementCurrency: string | null;
  ndfFixingDate: string | null;
  fixingPageCcy1: string | null;
  isCrossed: boolean;
  crossCurrency: string | null;
  fixingPageCcy2: string | null;
};

export type BulkTradeRfsRequestResponse = {};

export const requestBulkTradeRfs = (builder: WebapiEndpointBuilder) => {
  return builder.mutation<BulkTradeRfsRequestResponse, BulkTradeRfsRequestParams>({
    query: (params) => {
      const body = getBulkTradeRfsBody(params);

      return {
        url: '/fxBulk/rfs/request',
        method: 'POST',
        body,
      };
    },

    async onCacheEntryAdded(params, { cacheEntryRemoved, dispatch }) {
      // wait for the initial query to resolve before proceeding
      // await cacheDataLoaded;

      const handleReceiveQuoteReply = buildHandleReceiveQuoteReply(params.tabId, params.rfsId, dispatch);
      const handleReceiveQuoteAbort = buildHandleReceiveQuoteAbort(params.tabId, params.rfsId, dispatch);

      const signalRConnector = getSignalRConnector();

      signalRConnector.on('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteAbort', handleReceiveQuoteAbort);

      // to close the RFS stream, call:
      // closeBulkTradeRfs(tileId, dispatch)
      await cacheEntryRemoved;

      signalRConnector.off('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteAbort', handleReceiveQuoteAbort);
    },
  });
};

export const getRequestBulkTradeRfsMutationCacheKey = (tabId: string) => `bulk-trade-rfs-request-${tabId}`;

export function closeBulkTradeRfs(tabId: string, dispatch: AppDispatch) {
  dispatch(
    webapi.internalActions.removeMutationResult({ fixedCacheKey: getRequestBulkTradeRfsMutationCacheKey(tabId) }),
  );
  dispatch(bulkTradeSlice.actions.canceled({ tabId }));
}

export function getBulkTradeRfsBody(request: BulkTradeRfsRequestParams) {
  const legs: Array<BulkTradeRfsRequestLeg> = Object.entries(request.legs).reduce((allBodyLegs, [legId, leg]) => {
    // swap
    if (leg.type === 'Swap') {
      allBodyLegs.push({
        id: `${legId}-near`,
        product: leg.nearExpiryDateTenor === 'SP' ? 'FxSpot' : 'FxFwd',
        way: leg.way === 'BuyThenSell' ? 'Buy' : 'Sell',
        amount: leg.nearNotional ?? 0,
        paymentDate: leg.nearExpiryDate ?? '',
        paymentDateTenor: leg.nearExpiryDateTenor ?? '',
        ndFixingInfos: null, // NDF not supported yet for bulk trade in FX
      });

      allBodyLegs.push({
        id: `${legId}-far`,
        product: leg.farExpiryDateTenor === 'SP' ? 'FxSpot' : 'FxFwd',
        way: leg.way === 'BuyThenSell' ? 'Sell' : 'Buy',
        amount: leg.farNotional ?? 0,
        paymentDate: leg.farExpiryDate ?? '',
        paymentDateTenor: leg.farExpiryDateTenor ?? '',
        ndFixingInfos: null, // NDF not supported yet for bulk trade in FX
      });
    }

    // cash
    else {
      allBodyLegs.push({
        id: legId,
        product: leg.type === 'Spot' ? 'FxSpot' : 'FxFwd',
        way: leg.way,
        amount: leg.notional ?? 0,
        paymentDate: leg.maturityDate ?? '',
        paymentDateTenor: leg.maturityDateTenor ?? '',
        ndFixingInfos: null, // NDF not supported yet for bulk trade in FX
      });
    }

    return allBodyLegs;
  }, [] as Array<BulkTradeRfsRequestLeg>);

  return {
    fxBulkProduct: {
      currencyPair: request.currencyPair,
      amountCurrency: request.negotiatedCurrencyIndex,
      legs,
    },
    replyToStream: request.signalRConnectionId,
    rfsId: request.rfsId,
    requesterCounterpartyId: request.clientId,
  } satisfies BulkTradeRfsRequestBody;
}

// ███████╗██╗ ██████╗ ███╗   ██╗ █████╗ ██╗         ██████╗
// ██╔════╝██║██╔════╝ ████╗  ██║██╔══██╗██║         ██╔══██╗
// ███████╗██║██║  ███╗██╔██╗ ██║███████║██║         ██████╔╝
// ╚════██║██║██║   ██║██║╚██╗██║██╔══██║██║         ██╔══██╗
// ███████║██║╚██████╔╝██║ ╚████║██║  ██║███████╗    ██║  ██║
// ╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚═╝  ╚═╝╚══════╝    ╚═╝  ╚═╝

function buildHandleReceiveQuoteReply(tabId: string, rfsId: string, dispatch: AppDispatch) {
  return (reply: IQuoteReply<ReceivedBulkQuoteReply> | IQuoteWithdraw<ReceivedBulkQuoteReply>) => {
    if (reply.BulkQuoteReply?.RfsId !== rfsId) {
      return;
    }

    dispatch(
      bulkTradeSlice.actions.quoteReceived({
        tabId,
        rfsId,
        quote: reply.BulkQuoteReply,
        workerTimestamp: reply.workerTimestamp,
      }),
    );
  };
}

function buildHandleReceiveQuoteAbort(tabId: string, rfsId: string, dispatch: AppDispatch) {
  return (reject: IQuoteAbort) => {
    if (reject.RequestId !== rfsId) {
      return;
    }

    closeBulkTradeRfs(tabId, dispatch);

    if (isDefined(reject.QuoteReject)) {
      // is it the end of the RFS window ?
      if (reject.QuoteReject.ErrorCode === 'EM_TIMEOUT') {
        // it is not an error. the RFS is just over
        return;
      }

      console.warn('RFS Quote aborted:', reject.QuoteReject.ErrorMessage);

      dispatch(
        bulkTradeSlice.actions.rfsRequestFailed({
          tabId,
          code: reject.QuoteReject.ErrorCode,
          message: reject.QuoteReject.ErrorMessage,
          rfsId: reject.RequestId,
        }),
      );
    }
  };
}
